import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

const ICON = { success: CheckCircle2, warn: AlertTriangle, error: XCircle, info: Info };
const TONE = {
  success: "text-emerald-700",
  warn: "text-amber-700",
  error: "text-rose-700",
  info: "text-neutral-500",
};

// Poll cadence — every 60s is plenty for a Slack-style pulse without being
// aggressive on the API. We also poll once immediately on mount.
const POLL_MS = 60_000;
const LS_KEY = "jobgrid_admin_events_last_seen";

/**
 * Admin-only notification bell that lives in the sidebar. Shows a red dot
 * when there are events created AFTER the last time this browser opened the
 * dropdown. Click → dropdown with the 5 most recent events + "Bekijk alle".
 */
export default function AdminNotificationBell() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/events?limit=5");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Close on Escape key.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open]);

  const lastSeen = Number(localStorage.getItem(LS_KEY) || 0);
  const unseen = rows.filter((r) => {
    const ts = r.created_at ? new Date(r.created_at).getTime() : 0;
    return ts > lastSeen;
  });

  const toggle = () => {
    if (!open) {
      // Mark as seen when the user opens the dropdown.
      localStorage.setItem(LS_KEY, String(Date.now()));
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative" data-testid="admin-notify-bell">
      <button
        data-testid="admin-notify-bell-btn"
        onClick={toggle}
        title={t("admBellTooltip")}
        className="relative flex items-center justify-center border border-neutral-300 px-3 py-2 hover:border-neutral-900 transition-colors"
      >
        <Bell size={12} strokeWidth={1.5} />
        {unseen.length > 0 && (
          <span
            data-testid="admin-notify-bell-dot"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-rose-600 text-white text-[0.55rem] font-mono flex items-center justify-center rounded-full border border-white"
          >
            {unseen.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[65] bg-black/50"
            onClick={() => setOpen(false)}
            data-testid="admin-notify-backdrop"
          />
          {/* Centered modal */}
          <div
            data-testid="admin-notify-dropdown"
            role="dialog"
            aria-modal="true"
            className="fixed z-[66] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(30rem,calc(100vw-2rem))] max-h-[85vh] border border-neutral-900 bg-white shadow-2xl text-left flex flex-col"
          >
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between shrink-0">
              <p className="ed-eyebrow">{t("admBellTitle")}</p>
              <div className="flex items-center gap-3">
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900"
                  data-testid="admin-notify-view-all"
                >
                  {t("admOverviewEventsAll")} ›
                </Link>
                <button
                  data-testid="admin-notify-close"
                  onClick={() => setOpen(false)}
                  className="text-neutral-500 hover:text-neutral-900"
                  aria-label="Close"
                >
                  <X size={14} strokeWidth={1.6} />
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-neutral-500 italic font-serif" data-testid="admin-notify-empty">
                {t("admBellEmpty")}
              </p>
            ) : (
              <ol className="overflow-y-auto flex-1">
                {rows.map((r, i) => {
                  const Icon = ICON[r.level] || Info;
                  const tone = TONE[r.level] || TONE.info;
                  const ts = r.created_at ? new Date(r.created_at) : null;
                  return (
                    <li
                      key={r.id || i}
                      data-testid={`admin-notify-row-${i}`}
                      className="px-5 py-3.5 border-b border-neutral-100 last:border-b-0 flex items-start gap-3"
                    >
                      <Icon size={13} strokeWidth={1.75} className={`shrink-0 mt-0.5 ${tone}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-sm leading-snug break-words">{r.text}</p>
                        <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 mt-1">
                          {ts ? ts.toLocaleString() : "—"}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
